import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faSpinner, faSearch, faStore } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from '@/Components/ui/Tooltip';
import { geocodingService, LocationResult } from '@/services/geocoding.service';
import { GooglePlaceResult } from '@/models/incidentInBusiness';
import { useDebounce } from 'usehooks-ts';

interface AddressAutocompleteProps {
  value: string;
  onLocationSelect: (location: LocationResult) => void;
  onChange?: (value: string) => void;
  onGarageSelect?: (garage: GooglePlaceResult) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  searchType?: 'address' | 'garage';
  currentLocation?: { lat: number; lng: number };
  dataTestId?: string;
  label?: string;
  required?: boolean;
  tooltip?: string;
}

type SuggestionType = LocationResult | GooglePlaceResult;

function isGooglePlaceResult(item: SuggestionType): item is GooglePlaceResult {
  return 'placeId' in item;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onLocationSelect,
  onChange,
  onGarageSelect,
  placeholder = "Rechercher une adresse...",
  className = "",
  error,
  searchType = 'address',
  currentLocation,
  dataTestId,
  label,
  required = false,
  tooltip,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchQuery = useDebounce(inputValue, 300);

  useEffect(() => {
    setInputValue(value);
    setHasUserInteracted(false);
    if (value === '' || !value) {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  }, [value]);

  const search = useCallback(async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      let results: SuggestionType[] = [];

      if (searchType === 'garage') {
        results = await geocodingService.searchGarage(query, currentLocation);
      } else {
        results = await geocodingService.searchAddress(query, {
          country: 'fr',
          types: ['address', 'poi'],
          limit: 5
        });
      }

      setSuggestions(results);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error(`Error searching ${searchType}:`, error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchType, currentLocation]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setHasUserInteracted(true);

    if (onChange) {
      onChange(newValue);
    }

    setSelectedIndex(-1);

    if (newValue.length >= 3) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionSelect = (suggestion: SuggestionType) => {
    setJustSelected(true);

    if (isGooglePlaceResult(suggestion)) {
      setInputValue('');
      if (onGarageSelect) {
        onGarageSelect(suggestion);
      }
    } else {
      setInputValue(suggestion.address);
      onLocationSelect(suggestion);
    }

    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    setTimeout(() => setJustSelected(false), 500);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (justSelected) return;
    if (!hasUserInteracted) return;

    if (debouncedSearchQuery !== value) {
      search(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, value, search, justSelected, hasUserInteracted]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasValue = !!inputValue;

  return (
    <div ref={containerRef} className={`tw-relative ${className}`}>
      <div className="tw-relative">
        <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-3 tw-flex tw-items-center tw-pointer-events-none tw-z-10">
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faSearch}
            className={`tw-text-gray-400 ${isLoading ? 'tw-animate-spin' : ''}`}
          />
        </div>
        <input
          ref={inputRef}
          type="text"
          data-testid={dataTestId}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => hasUserInteracted && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={label || placeholder || ' '}
          className={`tw-w-full tw-pl-10 tw-pr-3 ${label ? 'tw-pt-5 tw-pb-1.5' : 'tw-py-1.5'} tw-text-sm tw-border tw-rounded-xl tw-peer tw-placeholder-transparent
            ${error ? 'tw-border-red-500' : 'tw-border-gray-300'}
            focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary`}
        />
        {label && (
          <label
            className={`tw-absolute tw-left-10 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${hasValue
                ? 'tw-top-1.5 tw-text-[11px] tw-text-affinicar-primary'
                : 'tw-top-[13px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px] peer-focus:tw-text-affinicar-primary'
              }
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}
      </div>

      {error && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="tw-absolute tw-z-10 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-max-h-60 tw-overflow-auto">
          {suggestions.map((suggestion, index) => {
            const isGarage = isGooglePlaceResult(suggestion);
            return (
              <button
                key={index}
                type="button"
                data-testid={`address-suggestion-${index}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`tw-w-full tw-text-left tw-px-4 tw-py-3 tw-border-b tw-border-gray-100 last:tw-border-b-0
                  hover:tw-bg-gray-50 tw-transition-colors tw-flex tw-items-start tw-gap-3
                  ${selectedIndex === index ? 'tw-bg-gray-50' : ''}`}
              >
                <FontAwesomeIcon
                  icon={isGarage ? faStore : faMapMarkerAlt}
                  className="tw-text-gray-400 tw-mt-0.5 tw-flex-shrink-0"
                />
                <div className="tw-flex-1">
                  {isGarage ? (
                    <>
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">
                        {suggestion.displayName}
                      </div>
                      <div className="tw-text-xs tw-text-gray-500">
                        {suggestion.formattedAddress}
                      </div>
                      {suggestion.rating && (
                        <div className="tw-text-xs tw-text-gray-400 tw-mt-1">
                          ⭐ {suggestion.rating} ({suggestion.userRatingCount} avis)
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="tw-text-sm tw-font-medium tw-text-gray-900">
                        {suggestion.address.split(',')[0]}
                      </div>
                      <div className="tw-text-xs tw-text-gray-500">
                        {suggestion.address.split(',').slice(1).join(',')}
                      </div>
                      {suggestion.postalCode && (
                        <div className="tw-text-xs tw-text-gray-400 tw-mt-1">
                          {suggestion.postalCode} {suggestion.city}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showSuggestions && inputValue.length >= 3 && suggestions.length === 0 && !isLoading && (
        <div className="tw-absolute tw-z-10 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-p-4">
          <p className="tw-text-sm tw-text-gray-500 tw-text-center">
            {searchType === 'garage'
              ? `Aucun garage trouvé pour "${inputValue}"`
              : `Aucune adresse trouvée pour "${inputValue}"`}
          </p>
        </div>
      )}
    </div>
  );
};
